import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography, Link,
  Grid, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import WarningIcon from '@mui/icons-material/Warning'
import { DuplicatesTable, isAbleToBeDeleted } from './duplicates-table'
import {
  getDuplicates,
  type DuplicatesResponse, type DuplicatePair, deleteDuplicates
} from 'services/person/international/duplicates/duplicatesService'
import { useGlobalMessageHandler } from 'components/common/hooks/use-user-messages-hook'
import { formatErrorFieldMessages } from 'interfaces'
import DeleteIcon from '@mui/icons-material/Delete'
import { type Month, Months } from 'helpers'
import { MonthSelect } from 'components'
import { getMonthByNumber } from 'helpers/monthHelper'

const getCurrentYear = (): number => new Date().getFullYear()
const generateYearOptions = (): number[] => {
  const currentYear = getCurrentYear()
  return Array.from({ length: currentYear - 2023 }, (_, i) => 2024 + i)
}

export const DuplicatesContent: React.FC = () => {
  const { showErrorMessage, showSuccessMessage } = useGlobalMessageHandler()
  const currentYear = getCurrentYear()
  const currentMonth = new Date().getMonth() + 1
  const emptyResponse = {
    associations: [],
    not_found_unique_ids: [],
    all_sheet_duplicate_pairs: []
  }

  const [year, setYear] = useState<number>(currentYear)
  const [month, setMonth] = useState<Month>(getMonthByNumber(currentMonth) ?? Months.JANUARY)
  const [duplicatesData, setDuplicatesData] = useState<DuplicatesResponse>(emptyResponse)
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState<string>('')

  const handleSearch = async (): Promise<void> => {
    setDuplicatesData(emptyResponse)
    setLoading(true)
    setLoadingMessage('Downloading Google sheet...')

    const requestPromise = getDuplicates(year, month.number)

    setTimeout(() => {
      setLoadingMessage('Querying database...')
    }, 4500)

    try {
      const response = await requestPromise
      if (response.succeeded && response.result != null) {
        setDuplicatesData(response.result)
        setHasSearched(true)
        showSuccessMessage('Duplicates fetched successfully')
      } else {
        showErrorMessage(formatErrorFieldMessages(response.errors))
        setDuplicatesData(emptyResponse)
        setHasSearched(false)
      }
    } catch (error) {
      showErrorMessage('Failed to fetch duplicates')
    } finally {
      setLoading(false)
      setLoadingMessage('')
    }
  }

  const handleDeleteAllDuplicates = async (): Promise<void> => {
    setLoading(true)
    try {
      const loserIds = duplicatesData.associations
        .filter(duplicate => isAbleToBeDeleted(duplicate))
        .map(duplicate => duplicate.loser.id)

      const response = await deleteDuplicates(loserIds, year, month.number)
      if (response.succeeded) {
        showSuccessMessage('All duplicates deleted successfully')
        void handleSearch()
      } else {
        showErrorMessage(formatErrorFieldMessages(response.errors))
      }
    } catch (error) {
      showErrorMessage('Failed to delete all duplicates')
    } finally {
      setLoading(false)
    }
  }

  const getSkippedDuplicatePairs = (): DuplicatePair[] => {
    return duplicatesData.all_sheet_duplicate_pairs.filter(pair =>
      duplicatesData.not_found_unique_ids.includes(pair.winner.value) ||
      duplicatesData.not_found_unique_ids.includes(pair.loser.value)
    )
  }

  const LoadingState: React.FC<{ loading: boolean, loadingMessage: string }> = ({ loading, loadingMessage }) => {
    if (!loading) return null

    const icon = loadingMessage.includes('Downloading')
      ? <CloudDownloadIcon sx={{
        animation: 'bounce 1s infinite',
        '@keyframes bounce': {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-5px)' }
        }
      }} />
      : <CircularProgress size={24} />

    return (
      <Box display="flex" alignItems="center" gap={2}>
        {icon}
        <Typography>{loadingMessage}</Typography>
      </Box>
    )
  }

  const getAvailableMonths = (): Month[] => {
    return Object.values(Months)
      .filter(month => year < currentYear || month.number <= currentMonth)
  }

  const allDuplicatesCanBeDeleted = duplicatesData.associations.length > 0 &&
    duplicatesData.associations.every(duplicate => isAbleToBeDeleted(duplicate))

  const MissingIdsSection: React.FC = () => {
    if (!hasSearched || duplicatesData.not_found_unique_ids.length === 0) return null

    const IdLink: React.FC<{ pair: DuplicatePair, isLoser: boolean }> = ({ pair, isLoser }) => {
      const value = isLoser ? pair.loser.value : pair.winner.value
      const link = isLoser ? pair.loser.cell_link : pair.winner.cell_link
      const isMissing = duplicatesData.not_found_unique_ids.includes(value)

      return (
        <Link
          href={link}
          target="_blank"
          sx={isMissing ? { color: 'error.main', fontWeight: 'bold' } : undefined}
        >
          {value}
        </Link>
      )
    }

    return (
      <Accordion sx={{ backgroundColor: '#f5cc87' }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
        >
          <Box display="flex" gap={1} alignItems="center">
            <WarningIcon/>
            <Typography>
              {duplicatesData.all_sheet_duplicate_pairs.length} duplicate {duplicatesData.all_sheet_duplicate_pairs.length === 1 ? 'pair' : 'pairs'} with type IE_ID downloaded from sheet. Cannot process {getSkippedDuplicatePairs().length} {getSkippedDuplicatePairs().length === 1 ? 'pair' : 'pairs'} because the following {duplicatesData.not_found_unique_ids.length !== 1 && duplicatesData.not_found_unique_ids.length} unique {duplicatesData.not_found_unique_ids.length === 1 ? 'ID was' : 'IDs were'} not found.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1}>
            {getSkippedDuplicatePairs().map((pair, index) => (
              <Grid item xs={4} key={index}>
                <Box display="flex" gap={1}>
                  <IdLink pair={pair} isLoser={false} />
                  <Typography>←</Typography>
                  <IdLink pair={pair} isLoser={true} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
    <Box maxWidth={1700} sx={{ pb: 1, pt: 1 }}>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Box display="flex" gap={2} justifyContent="center" alignItems="center">
              <FormControl sx={{ minWidth: 120 }} disabled={loading}>
                <InputLabel id="year-select-label">Year</InputLabel>
                <Select
                  labelId="year-select-label"
                  id="year-select"
                  value={year}
                  label="Year"
                  onChange={(e) => { setYear(Number(e.target.value)) }}
                >
                  {generateYearOptions().map((yearOption) => (
                    <MenuItem key={yearOption} value={yearOption}>
                      {yearOption}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <MonthSelect
                value={month}
                onChange={(selectedMonth: Month) => { setMonth(selectedMonth) }}
                showNone={false}
                availableMonths={getAvailableMonths()}
                disabled={loading}
                label="Month"
              />

              <Button
                variant="contained"
                onClick={() => { void handleSearch() }}
                disabled={loading}
              >
                Process Sheet Duplicates
              </Button>
              {LoadingState({ loading, loadingMessage })}
            </Box>
            <MissingIdsSection />
            {!loading && hasSearched && allDuplicatesCanBeDeleted && (<Box display="flex" justifyContent="center" mb={2}>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => { void handleDeleteAllDuplicates() }}
                  disabled={loading}
                >
                  Delete All Duplicates
                </Button>
              </Box>
            )}
            {!loading && hasSearched && <DuplicatesTable duplicates={duplicatesData.associations} loading={loading} />}
          </Stack>
        </CardContent>
      </Card>
    </Box>
    </Box>
  )
}
